import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { createPortal } from "react-dom";

export default function Loading() {
  return createPortal(
    <div className="fixed inset-0 z-50 flex h-screen w-screen items-center justify-center bg-black/10">
      <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-t border-slate-300" />
    </div>,
    document.body,
  );
}

export function LoadingItem() {
  return (
    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-t border-slate-300" />
  );
}

interface ChartAreaLoaderProps {
  title?: string;
  subtitle?: string;
}

const chartLoaderStyles = `
  @keyframes chart-loader-shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes chart-loader-draw {
    0% {
      stroke-dashoffset: 180;
      opacity: 0.35;
    }
    40% {
      opacity: 1;
    }
    100% {
      stroke-dashoffset: 0;
      opacity: 0.5;
    }
  }

  @keyframes chart-loader-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-4px); }
  }

  @keyframes chart-loader-scan {
    0% { transform: translateX(-20%); opacity: 0; }
    15% { opacity: 0.55; }
    50% { opacity: 0.65; }
    100% { transform: translateX(120%); opacity: 0; }
  }
`;

const yAxisLabels = ["10K", "8K", "6K", "4K", "2K", "0"];

const chartSeries = [
  {
    d: "M0,246 C80,244 120,242 160,228 C220,206 268,156 332,134 C398,112 464,102 524,100 C596,98 652,114 720,126 C794,140 856,158 920,180 C960,194 990,208 1000,214",
    color: "#94A3B8",
    glow: "#94A3B8",
    delay: "0s",
  },
  {
    d: "M0,258 C88,256 126,252 170,238 C230,218 276,174 338,150 C402,124 462,116 520,114 C594,112 650,124 714,136 C786,150 850,168 918,188 C962,200 992,212 1000,216",
    color: "#CBD5E1",
    glow: "#CBD5E1",
    delay: "0.18s",
  },
  {
    d: "M0,286 C82,284 128,282 172,268 C232,248 276,208 342,188 C402,170 462,166 526,166 C594,166 652,172 712,182 C780,194 846,208 918,220 C964,226 994,230 1000,232",
    color: "#E6E9EE",
    glow: "#E6E9EE",
    delay: "0.36s",
  },
  {
    d: "M0,232 C76,230 124,226 164,214 C224,196 274,162 336,142 C396,122 462,114 522,114 C590,114 652,124 720,136 C790,150 858,168 920,184 C964,194 994,204 1000,208",
    color: "#B6C2C7",
    glow: "#B6C2C7",
    delay: "0.54s",
  },
];

const legendWidths = [84, 98, 88, 104, 92, 112];

export function ChartAreaLoader({
  title = "Loading chart",
  subtitle = "Preparing chart structure and live insights...",
}: ChartAreaLoaderProps) {
  return (
    <div
      className="h-full w-full"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <style>{chartLoaderStyles}</style>

      <div className="h-full w-full space-y-4">
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#e6eef6] via-[#f1f5f9] to-[#f8fafc]" />
          <div className="pointer-events-none absolute -right-12 top-12 h-28 w-28 animate-pulse rounded-full bg-[#94A3B8]/6 blur-2xl" />
          <div className="pointer-events-none absolute -left-12 bottom-10 h-32 w-32 animate-pulse rounded-full bg-[#E6E9EE]/6 blur-2xl [animation-delay:900ms]" />

          <div className="flex flex-col gap-4 border-b border-slate-100 px-4 py-4 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-3">
                <div className="h-5 w-56 animate-pulse rounded-full bg-slate-200/80" />
                <div className="h-3 w-72 max-w-[80vw] animate-pulse rounded-full bg-slate-100" />
              </div>

              <div className="flex items-center gap-3">
                <div className="hidden h-9 w-20 animate-pulse rounded-full border border-slate-200 bg-slate-50/80 sm:block" />
                <div className="hidden h-9 w-24 animate-pulse rounded-full border border-slate-200 bg-slate-50/80 sm:block" />
                <div className="h-9 w-32 animate-pulse rounded-full border border-slate-200 bg-slate-50/80" />
              </div>
            </div>
          </div>

          <div className="min-h-0 w-full flex-1 px-4 pb-4 pt-4 sm:px-6 sm:pb-6">
            <div className="relative flex h-full w-full min-h-[160px] flex-col overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-b from-white via-white to-slate-50/70">
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 48%, transparent 100%)",
                  animation: "chart-loader-shimmer 2.5s ease-in-out infinite",
                }}
              />

              <div className="grid h-full w-full grid-cols-[48px_1fr] gap-2 px-2 pt-3 sm:grid-cols-[72px_1fr] sm:gap-3 sm:px-3 sm:pt-4">
                <div className="flex h-full min-h-[160px] w-full flex-col justify-between pb-8 pt-2 text-[10px] text-slate-400 sm:pb-10 sm:text-[11px]">
                  {yAxisLabels.map((label) => (
                    <span key={label} className="tabular-nums">
                      {label}
                    </span>
                  ))}
                </div>

                <div className="relative h-full min-h-[160px] w-full overflow-hidden rounded-xl">
                  <div className="absolute inset-0">
                    {[12, 26, 40, 54, 68, 82, 96].map((top) => (
                      <span
                        key={top}
                        className="absolute left-0 right-0 border-t border-slate-200/80"
                        style={{ top: `${top}%` }}
                      />
                    ))}
                    <span className="absolute left-[31%] top-0 h-full border-l border-dashed border-slate-300/70" />
                    <span
                      className="absolute top-0 h-full w-px bg-gradient-to-b from-transparent via-[#34656d]/70 to-transparent"
                      style={{
                        animation:
                          "chart-loader-scan 4.4s ease-in-out infinite",
                      }}
                    />
                  </div>

                  <svg
                    className="absolute inset-0 h-full w-full"
                    viewBox="0 0 1000 320"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <defs>
                      {chartSeries.map((series) => (
                        <linearGradient
                          key={series.color}
                          id={`chart-loader-gradient-${series.color.replace(
                            "#",
                            "",
                          )}`}
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop
                            offset="0%"
                            stopColor={series.color}
                            stopOpacity="0.15"
                          />
                          <stop
                            offset="55%"
                            stopColor={series.color}
                            stopOpacity="0.95"
                          />
                          <stop
                            offset="100%"
                            stopColor={series.glow}
                            stopOpacity="0.25"
                          />
                        </linearGradient>
                      ))}
                      <filter id="chart-loader-glow">
                        <feGaussianBlur stdDeviation="1.6" result="blur" />
                        <feColorMatrix
                          in="blur"
                          type="matrix"
                          values="1 0 0 0 0
                                0 1 0 0 0
                                0 0 1 0 0
                                0 0 0 0.18 0"
                        />
                      </filter>
                    </defs>

                    {chartSeries.map((series) => {
                      const gradientId = `chart-loader-gradient-${series.color.replace(
                        "#",
                        "",
                      )}`;

                      return (
                        <g key={series.color}>
                          <path
                            d={series.d}
                            fill="none"
                            stroke={series.glow}
                            strokeOpacity="0.08"
                            strokeWidth="6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="120"
                            strokeDashoffset="120"
                            filter="url(#chart-loader-glow)"
                            pathLength={100}
                            style={{
                              animation:
                                "chart-loader-draw 3.8s ease-in-out infinite",
                              animationDelay: series.delay,
                            }}
                          />
                          <path
                            d={series.d}
                            fill="none"
                            stroke={`url(#${gradientId})`}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="120"
                            strokeDashoffset="120"
                            pathLength={100}
                            style={{
                              animation:
                                "chart-loader-draw 4.1s ease-in-out infinite",
                              animationDelay: series.delay,
                            }}
                          />
                        </g>
                      );
                    })}
                  </svg>

                  <div
                    className="absolute left-1/2 top-[24%] -translate-x-1/2 rounded-xl border border-white/70 bg-white/95 px-3 py-2 shadow-[0_16px_40px_rgba(15,23,42,0.12)] backdrop-blur sm:left-[44%] sm:top-[28%] sm:translate-x-0 sm:rounded-2xl sm:px-4 sm:py-3"
                    style={{
                      animation: "chart-loader-float 3.6s ease-in-out infinite",
                    }}
                  >
                    <div className="mb-2 h-2.5 w-28 rounded-full bg-slate-200/90 animate-pulse sm:h-3 sm:w-36" />
                    <div className="space-y-2">
                      {[56, 48, 64, 52].map((width, index) => (
                        <div key={width} className="flex items-center gap-3">
                          <span className="h-2 w-2 rounded-full bg-slate-200 sm:h-2.5 sm:w-2.5" />
                          <span
                            className="h-2 rounded-full bg-slate-200 sm:h-2.5"
                            style={{
                              width: `${width + (index % 2 === 0 ? 18 : 12)}px`,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 px-3 pb-3 text-[11px] text-slate-400">
                <div className="h-3 w-24 rounded-full bg-slate-200/90 animate-pulse" />
                <div className="h-3 w-32 rounded-full bg-slate-200/90 animate-pulse" />
              </div>
            </div>

            <div className="mt-3 hidden flex-wrap items-center justify-center gap-3 sm:mt-4 sm:flex [@media(max-height:760px)]:hidden">
              {legendWidths.map((width, index) => (
                <div
                  key={`${width}-${index}`}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-sm"
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{
                      backgroundColor:
                        chartSeries[index % chartSeries.length].color,
                      boxShadow: `0 0 0 4px ${chartSeries[index % chartSeries.length].color}14`,
                    }}
                  />
                  <span
                    className="h-2.5 rounded-full bg-slate-200/90"
                    style={{ width: `${width}px` }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end px-4 pb-3 sm:px-6 sm:pb-4">
            <div className="hidden rounded-full border border-slate-200 bg-white/90 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.24em] text-slate-400 shadow-sm sm:block [@media(max-height:760px)]:hidden">
              Loading chart structure
            </div>
          </div>
        </div>
      </div>

      <p className="sr-only">
        {title}. {subtitle}
      </p>
    </div>
  );
}

export function LegacyChartAreaLoader({
  title = "Loading Data",
  subtitle = "Fetching the latest insights...",
}: ChartAreaLoaderProps) {
  return (
    <div
      className="flex h-[710px] w-full flex-col items-center justify-center gap-1"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <DotLottieReact
        src="https://lottie.host/e5d43299-8a12-4fd2-9cd9-17750f9f56c5/lZjt4YNRaX.lottie"
        loop
        autoplay
        style={{ width: 200, height: 200 }}
      />
      <p className="m-0 text-[13px] font-semibold uppercase tracking-[0.06em] text-[#F1935C]">
        Preparing your latest insights
      </p>
      <p className="m-0 text-[12px] text-slate-400">{subtitle}</p>
      <span className="sr-only">
        {title}. {subtitle}
      </span>
    </div>
  );
}
